"use client";

import { useQuery } from "@tanstack/react-query";
import { useWeb3 } from "logic/components";
import { toUnits } from "logic/utils";
import { useSearchParams } from "next/navigation";

import { getBorrowerLoans } from "../data/getBorrowerLoans";

export class UnauthorizedLoansError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "UnauthorizedLoansError";
  }
}

export type LoansPageState = ReturnType<typeof useLoansPageState>;

export function useLoansPageState() {
  const { address } = useWeb3();

  const {
    data: loans,
    error: loansError,
    isLoading: isLoansLoading,
    refetch: refetchLoans,
  } = useQuery({
    refetchOnWindowFocus: true,
    queryKey: ["borrowerLoansV2", address] as const,
    queryFn: async ({ queryKey }) => {
      const [, address] = queryKey;
      if (!address) throw new Error("No address found");
      const result = await getBorrowerLoans(address);
      if (result.unauthorized) throw new UnauthorizedLoansError();
      return result.loans;
    },
    retry: (failureCount, error) => !(error instanceof UnauthorizedLoansError) && failureCount < 3,
    enabled: Boolean(address),
  });

  console.log("[useLoansPageState] loans:", loans);

  const selectedLoanId = useSearchParams().get("loan");
  const selectedLoan = loans?.find((loan) => loan.loanTermsHash === selectedLoanId) ?? null;

  return {
    loans: loans ?? null,
    loansError,
    isLoansLoading,
    refetchLoans,
    selectedLoan,
    usdaiLocked: loans?.reduce(
      (acc, loan) =>
        loan.metadata.escrowed === "offchain" ? acc : acc + toUnits(loan.metadata.debtReserveAccount ?? 0),
      0n,
    ),
  };
}
